import {noop} from '@benchling/js-utils';
import moment from 'moment';
import React from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {CreateAnalysisModal} from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import {SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {CREATE_ANALYSIS_NO_PERMISSION_MESSAGE} from 'studies/typed/utils/analysis/analysisUtils';
import {createAnalysisForStudy} from 'studies/typed/utils/analysis/createAnalysisForStudy';
import ProcedureBasedStudyAnalysisModalBanner from 'studies/typed/utils/analysis/ProcedureBasedStudyAnalysisModalBanner';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as DateUtil from 'util/DateUtil';

const STUDY_ARCHIVED_MESSAGE = 'Unarchive your study to generate an analysis';

/**
 * Props for AnalyzeProcedureRunButton component.
 *
 * @package exported for testing.
 */
export interface AnalyzeProcedureRunButtonProps {
  procedureRunId: string;
  procedureRunName: string;
  isProcedureRunArchived: boolean;
  procedureType: ProcedureType;
  studyId: string;
  studyName: string;
  studyHumanId: string;
  isStudyArchived: boolean;
  studyType: StudyType;
  actionOrigin: ActionOrigin;
}

export default function AnalyzeProcedureRunButton(props: AnalyzeProcedureRunButtonProps): JSX.Element {
  let onClickHandler: () => void = noop;
  assert(props.procedureRunId != null, 'Procedure Run is not selected');

  const {
    studyId,
    studyName,
    studyHumanId,
    isStudyArchived,
    studyType,
    procedureRunId,
    procedureRunName,
    isProcedureRunArchived,
    procedureType,
    actionOrigin,
  } = props;
  const getInitialAnalysisName = (): string =>
    `${studyName}_${procedureRunName}_${DateUtil.readableTime(moment(), {
      showTime: true,
    })}`;

  onClickHandler = async () => {
    await legacyRunModal(CreateAnalysisModal, {
      actionOrigin,
      onCreateAnalysis: (data: CreateAnalysisParams) =>
        createAnalysisForStudy(data, {actionOrigin, studyType}),
      initialStudySearchItems: [{id: studyId, name: studyName, humanId: studyHumanId}],
      disableEditStudies: true,
      title: 'Generate analysis',
      initialAnalysisName: getInitialAnalysisName,
      headerInlineBanner: <ProcedureBasedStudyAnalysisModalBanner studyType={studyType} />,
      hideDataSharingWarningBanner: true,
      initialSeedDataConfig: {
        type: SeedDataConfigType.PROCEDURE_RUN,
        data: {
          procedureRunId,
        },
      },
    });
  };

  const hasAnalyzePermission: boolean = isFeatureEnabledForProcedureType(
    procedureType,
    ProceduresFeature.CREATE_ANALYSIS
  );
  const buttonDisabledDueToArchivedStudy = isStudyArchived;
  const buttonDisabledDueToArchivedRun = isProcedureRunArchived;
  const buttonDisabledDueToPermissions = !hasAnalyzePermission;
  const buttonDisabled: boolean =
    buttonDisabledDueToPermissions || buttonDisabledDueToArchivedStudy || buttonDisabledDueToArchivedRun;

  let tooltipText: string | null = null;
  if (buttonDisabledDueToArchivedStudy) {
    // Creating an analysis links it back to the study via a studyable association, which
    // triggers an auth error when the parent study is archived.
    tooltipText = STUDY_ARCHIVED_MESSAGE;
  } else if (buttonDisabledDueToArchivedRun) {
    tooltipText = `Unarchive your ${getProcedureRunUIStringForStudyType(studyType)} to generate an analysis`;
  } else if (buttonDisabledDueToPermissions) {
    tooltipText = CREATE_ANALYSIS_NO_PERMISSION_MESSAGE;
  }

  return (
    <Tooltip text={tooltipText} isDisabled={!buttonDisabled || tooltipText == null}>
      <Button
        dataTestAttrs={{
          component: 'AnalyzeProcedureRunButton',
          element: 'root',
        }}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MEDIUM}
        onClick={onClickHandler}
        text="Analyze"
        icon={ModelTypeConstants.ANALYSIS}
        disabled={buttonDisabled}
      />
    </Tooltip>
  );
}
